<template>
  <div class="anamnesis-subset">
    <!-- Header de collection contextuel -->
    <div v-if="collection" class="collection-header bg-white border border-gray-200 rounded-lg px-6 py-4 mb-6 shadow-sm flex items-center justify-between">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">{{ collection.name }}</h2>
        <p v-if="collection.description" class="text-gray-500 text-base mt-1 max-w-2xl">{{ collection.description }}</p>
      </div>
      <div class="flex items-center space-x-4">
        <div v-if="collection.created_at" class="text-gray-400 text-sm whitespace-nowrap">
          {{ new Date(collection.created_at).toLocaleDateString('fr-FR') }}
        </div>
        <button
          v-if="canEditCollection"
          class="ml-2 p-2 rounded-md border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          :title="'Éditer la collection'"
          aria-label="Éditer la collection"
          @click="openEditModal"
        >
          <PencilIcon class="h-5 w-5" />
        </button>
      </div>
    </div>
    <CollectionEditor
      v-if="collection"
      v-model="showEditorModal"
      :collection-to-edit="collection"
      @collection-saved="closeEditModal"
    />

    <!-- Toolbar réutilisable -->
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="isCardView"
      :search-query="searchQuery"
      search-placeholder="Rechercher dans les mémos..."
      :show-create-button="collection ? canEditCollection : true"
      :create-button-text="collection ? 'Ajouter des mémos à cette collection' : 'Nouveau MEMoS'"
      :show-mobile-menu="showMobileMenu"
      @update:sort-by="handleUpdateSortBy"
      @update:search-query="handleUpdateSearchQuery"
      @toggle-sort-direction="handleToggleSortDirection"
      @toggle-view="handleToggleView"
      @toggle-mobile-menu="handleToggleMobileMenu"
      @create="handleCreateMemo"
    >
      <!-- Slot checkbox : Checkbox "Tout sélectionner" -->
      <template #selection-checkbox>
        <div v-if="paginatedMemos.length > 0" class="flex items-center">
          <input
            type="checkbox"
            class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            :checked="areAllVisibleMemosSelected"
            :indeterminate="isAnyMemoSelected && !areAllVisibleMemosSelected"
            @change="toggleSelectAll"
          />
        </div>
      </template>

      <!-- Slot filtres : Filtres spécifiques aux mémos -->
      <template #filters>
        <div class="flex items-center space-x-3">
          <!-- Dropdown Statut (Brouillon/Validé/Modèle/Fiche) -->
          <div ref="statusDropdownRef" class="relative">
            <button
              class="px-3 py-2 bg-white border rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2 min-w-[120px] justify-between"
              @click="toggleStatusDropdown"
            >
              <div class="flex items-center space-x-1.5">
                <component 
                  :is="statusFilterIcon" 
                  class="h-4 w-4"
                  :class="statusFilterColor"
                />
                <span>Type : {{ statusFilterLabel }}</span>
              </div>
              <ChevronDownIcon 
                class="h-4 w-4 transition-transform duration-200"
                :class="{ 'transform rotate-180': showStatusDropdown }"
              />
            </button>
            
            <div
              v-show="showStatusDropdown"
              class="absolute top-full left-0 mt-1 w-56 bg-white border rounded-md shadow-lg z-10 py-1"
            >
                             <!-- Section État -->
               <div class="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b">
                 État de validation
               </div>
               
               <button
                 class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                 :class="{ 'bg-blue-50 text-blue-700': showDrafts }"
                 @click="toggleDraftStatus"
               >
                 <FileEditIcon class="h-4 w-4 text-orange-600" />
                 <span>Brouillons</span>
                 <CheckIcon 
                   v-if="showDrafts"
                   class="h-4 w-4 ml-auto text-blue-600"
                 />
               </button>
               
               <button
                 class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                 :class="{ 'bg-blue-50 text-blue-700': showValidated }"
                 @click="toggleValidatedStatus"
               >
                 <FileCheckIcon class="h-4 w-4 text-green-600" />
                 <span>Validés</span>
                 <CheckIcon 
                   v-if="showValidated"
                   class="h-4 w-4 ml-auto text-blue-600"
                 />
               </button>
               
               <!-- Section Type -->
               <div class="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-t">
                 Type de contenu
               </div>
               
               <button
                 class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                 :class="{ 'bg-blue-50 text-blue-700': showModels }"
                 @click="toggleModelType"
               >
                 <BookTemplateIcon class="h-4 w-4 text-purple-600" />
                 <span>Modèles</span>
                 <CheckIcon 
                   v-if="showModels"
                   class="h-4 w-4 ml-auto text-blue-600"
                 />
               </button>
               
               <button
                 class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                 :class="{ 'bg-blue-50 text-blue-700': showSheets }"
                 @click="toggleSheetType"
               >
                 <FileIcon class="h-4 w-4 text-pink-600" />
                 <span>Fiches</span>
                 <CheckIcon 
                   v-if="showSheets"
                   class="h-4 w-4 ml-auto text-blue-600"
                 />
               </button>
            </div>
          </div>

          <!-- Dropdown Visibilité -->
          <div ref="visibilityDropdownRef" class="relative">
            <button
              class="px-3 py-2 bg-white border rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2 min-w-[100px] justify-between"
              @click="toggleVisibilityDropdown"
            >
              <div class="flex items-center space-x-1.5">
                <component 
                  :is="visibilityFilterIcon" 
                  class="h-4 w-4"
                  :class="visibilityFilterColor"
                />
                <span>Vue : {{ visibilityFilterLabel }}</span>
              </div>
              <ChevronDownIcon 
                class="h-4 w-4 transition-transform duration-200"
                :class="{ 'transform rotate-180': showVisibilityDropdown }"
              />
            </button>
            
            <div
              v-show="showVisibilityDropdown"
              class="absolute top-full left-0 mt-1 w-40 bg-white border rounded-md shadow-lg z-10 py-1"
            >
              <button
                v-for="option in visibilityOptions"
                :key="option.value"
                class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                :class="{ 'bg-blue-50 text-blue-700': visibilityFilter === option.value }"
                @click="setVisibilityFilter(option.value)"
              >
                <component :is="option.icon" class="h-4 w-4" />
                <span>{{ option.label }}</span>
                <CheckIcon 
                  v-if="visibilityFilter === option.value"
                  class="h-4 w-4 ml-auto text-blue-600"
                />
              </button>
            </div>
          </div>

          <!-- Dropdown Propriétaire (si activé) -->
          <div v-if="showOwnershipFilter" ref="ownershipDropdownRef" class="relative">
            <button
              class="px-3 py-2 bg-white border rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2 min-w-[100px] justify-between"
              @click="toggleOwnershipDropdown"
            >
              <div class="flex items-center space-x-1.5">
                <component 
                  :is="ownershipFilterIcon" 
                  class="h-4 w-4"
                  :class="ownershipFilterColor"
                />
                <span>Statut : {{ ownershipFilterLabel }}</span>
              </div>
              <ChevronDownIcon 
                class="h-4 w-4 transition-transform duration-200"
                :class="{ 'transform rotate-180': showOwnershipDropdown }"
              />
            </button>
            
            <div
              v-show="showOwnershipDropdown"
              class="absolute top-full left-0 mt-1 w-40 bg-white border rounded-md shadow-lg z-10 py-1"
            >
              <button
                v-for="option in ownershipOptions"
                :key="option.value"
                class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                :class="{ 'bg-blue-50 text-blue-700': ownershipFilter === option.value }"
                @click="setOwnershipFilter(option.value)"
              >
                <component :is="option.icon" class="h-4 w-4" />
                <span>{{ option.label }}</span>
                <CheckIcon 
                  v-if="ownershipFilter === option.value"
                  class="h-4 w-4 ml-auto text-blue-600"
                />
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- Slot actions : Actions contextuelles -->
      <template #actions>
        <!-- Actions vides car on utilise maintenant le bouton de création de la toolbar -->
      </template>

      <!-- Slot mobile-content -->
      <template #mobile-content>
        <!-- Contenu du menu mobile -->
        <div class="space-y-2">
          <div class="text-sm font-medium text-gray-700">Filtres mobiles</div>
          <!-- Ici on pourrait ajouter une version mobile des filtres si nécessaire -->
        </div>
      </template>
    </SubsetToolbar>

    <!-- Affichage du nombre d'éléments sélectionnés -->
    <div v-if="isSelectionMode && selectedMemos.length > 0" class="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <CheckIcon class="h-4 w-4 text-blue-600" />
        <span class="text-sm font-medium text-blue-800">
          {{ selectedMemos.length }} mémo{{ selectedMemos.length > 1 ? 's' : '' }} sélectionné{{ selectedMemos.length > 1 ? 's' : '' }}
        </span>
      </div>
      <div class="flex items-center space-x-2">
        <button
          v-if="collection"
          class="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 border border-red-200 rounded text-xs hover:bg-red-200 transition-colors"
          @click="handleRemoveFromCollection"
        >
          <TrashIcon class="h-3 w-3 mr-1" />
          Retirer
        </button>
        
        <!-- Actions Dropdown compact -->
        <div ref="actionsDropdownRef" class="relative">
          <button
            class="inline-flex items-center px-2 py-1 bg-white border border-blue-200 rounded text-xs text-blue-700 hover:bg-blue-50 transition-colors duration-200 space-x-1"
            @click="toggleActionsDropdown"
          >
            <span>Actions</span>
            <ChevronDownIcon class="h-3 w-3" />
          </button>
          <div
            v-show="showActionsDropdown"
            class="absolute top-full right-0 mt-1 w-64 bg-white border rounded-md shadow-lg z-10 py-1"
          >
            <button
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
              @click="handleExportSelection"
            >
              <DownloadIcon class="h-4 w-4 text-gray-500" />
              <span>Exporter la sélection</span>
            </button>
            
            <button
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
              :class="{ 'opacity-50 cursor-not-allowed': !canDeleteSelectedMemos }"
              :disabled="!canDeleteSelectedMemos"
              @click="handleDeleteSelection"
            >
              <TrashIcon class="h-4 w-4 text-red-500" />
              <span>Supprimer la sélection</span>
            </button>
            
            <div class="my-1 border-t border-gray-100"></div>

            <div class="px-3 pt-2 pb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Ajouter à une collection
            </div>
            
            <div class="max-h-40 overflow-y-auto">
              <button
                v-for="col in collections"
                :key="col.uuid"
                class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
                @click="handleAddMemosToCollection(col.uuid)"
              >
                <span>{{ col.name }}</span>
              </button>
            </div>

            <div class="my-1 border-t border-gray-100"></div>
            
            <button
              class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 transition-colors duration-150"
              @click="handleOpenCollectionCreator"
            >
              <PlusIcon class="h-4 w-4 text-gray-500" />
              <span>Nouvelle collection...</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Liste des memos -->
    <div v-if="paginatedMemos.length > 0" :class="{ 'anamnesis-grid': isCardView, 'anamnesis-table': !isCardView }">
      <MemosCard
        v-for="memo in paginatedMemos"
        :key="memo.uuid"
        :memo="memo as any"
        :view-mode="isCardView ? 'card' : 'table'"
        :show-delete-button="showDeleteButton"
        :selectable="true"
        :selected="isMemoSelected(memo.uuid)"
        :selection-mode="isSelectionMode"
        @view="handleView"
        @edit="handleEdit"
        @delete="handleDelete"
        @toggle-selection="toggleMemoSelection"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucun mémo trouvé</p>
    </div>

    <!-- Compteur de mémos -->
    <div class="flex justify-end mt-4 mb-2">
      <p class="text-sm text-gray-400">
        {{ filteredMemos.length }} mémo{{ filteredMemos.length > 1 ? 's' : '' }} trouvé{{ filteredMemos.length > 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredMemos.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <button
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handlePreviousPage"
      >
        Précédent
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handleNextPage"
      >
        Suivant
      </button>
    </div>

    <CollectionEditor
      v-model="isCollectionEditorOpen"
      :collection-to-edit="null"
      @collection-saved="handleCollectionSaved"
    />

    <!-- Modal de sélection de mémos -->
    <MemoSelectionModal
      :is-open="isMemoSelectionModalOpen"
      :collection-name="collection?.name || ''"
      :collection-uuid="collection?.uuid || ''"
      :all-memos="allMemos"
      :current-user-memos="currentUserMemos"
      :existing-memos-in-collection="existingMemosInCollection"
      @close="closeMemoSelectionModal"
      @add-memos="handleAddMemosFromModal"
      @update-collection="handleUpdateCollectionFromModal"
    />
  </div>
</template>

<script setup lang="ts">
import type { MemoCollection } from '@/api/types/memos/collections';
import { useAuthStore } from '@/stores/auth';
import { useCollectionsStore } from '@/stores/collections';
import {
    BookTemplate as BookTemplateIcon,
    Check as CheckIcon,
    ChevronDown as ChevronDownIcon,
    Download as DownloadIcon,
    Eye as EyeIcon,
    FileCheck as FileCheckIcon,
    FileEdit as FileEditIcon,
    File as FileIcon,
    Globe as GlobeIcon,
    Pencil as PencilIcon,
    Plus as PlusIcon,
    Shield as ShieldIcon,
    Trash as TrashIcon,
    User as UserIcon,
    Users as UsersIcon
} from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import type { Memo } from '../../api/types/memos/types';
import type { SortOption } from '../UI/SubsetToolbar.vue';
import SubsetToolbar from '../UI/SubsetToolbar.vue';
import MemosCard from '../cards/MemosCard.vue';
import CollectionEditor from '../collections/CollectionEditor.vue';
import MemoSelectionModal from '../modals/MemoSelectionModal.vue';

const showEditorModal = ref(false);

function openEditModal() {
  if (canEditCollection.value) {
    showEditorModal.value = true;
  }
}
function closeEditModal() {
  showEditorModal.value = false;
}

// Props avec types stricts
interface Props {
  memos: Memo[];
  showToolbar?: boolean;
  showDeleteButton?: boolean;
  showOwnershipFilter?: boolean;
  defaultView?: 'card' | 'table';
  defaultSort?: 'date' | 'title' | 'location' | 'material' | 'schema_type';
  collection?: MemoCollection | undefined;
  allAccessibleMemos?: Memo[]; // Tous les mémos accessibles pour le modal
}

const props = withDefaults(defineProps<Props>(), {
  memos: () => [],
  showToolbar: true,
  showDeleteButton: false,
  showOwnershipFilter: true,
  defaultView: 'card',
  defaultSort: 'date',
  collection: undefined, // Ajout du default explicite pour la prop 'collection'
  allAccessibleMemos: () => [], // Default pour tous les mémos accessibles
});

// Events avec types stricts
const emit = defineEmits<{
  view: [memo: Memo];
  edit: [memo: Memo];
  delete: [memo: Memo];
  export: [memos: Memo[]];
  'refresh-data': [];
  'collection-memo-added': [data: { 
    memoCount: number; 
    collectionName: string;
    alreadyInCollection?: number;
    errors?: number;
  }];
  'collection-memo-removed': [data: { memoCount: number; collectionName: string }];
  'create-memo': [];
  'delete-memos': [memoUuids: string[]];
}>();

// Types pour les filtres
type VisibilityFilter = 'all' | 'public' | 'private';
type OwnershipFilter = 'all' | 'my' | 'public';
type SortDirection = 'asc' | 'desc';
type SortBy = 'date' | 'title' | 'location' | 'material' | 'schema_type';

// État réactif avec types stricts
const storedView: string | null = localStorage.getItem('memosViewMode');
const isCardView: Ref<boolean> = ref(storedView ? storedView === 'card' : props.defaultView === 'card');

const searchQuery: Ref<string> = ref('');
const sortBy: Ref<SortBy> = ref(props.defaultSort);
const sortDirection: Ref<SortDirection> = ref('asc');
const showDrafts: Ref<boolean> = ref(true);
const showValidated: Ref<boolean> = ref(true);
const showModels: Ref<boolean> = ref(true);
const showSheets: Ref<boolean> = ref(true);
const visibilityFilter: Ref<VisibilityFilter> = ref('all');
const ownershipFilter: Ref<OwnershipFilter> = ref('all');
const currentPage: Ref<number> = ref(1);
const itemsPerPage: Ref<number> = ref(50);
const showMobileMenu: Ref<boolean> = ref(false);

// État des dropdowns
const showStatusDropdown: Ref<boolean> = ref(false);
const showVisibilityDropdown: Ref<boolean> = ref(false);
const showOwnershipDropdown: Ref<boolean> = ref(false);
const showActionsDropdown: Ref<boolean> = ref(false);
const isCollectionEditorOpen: Ref<boolean> = ref(false);
const isMemoSelectionModalOpen: Ref<boolean> = ref(false);

// Refs pour les dropdowns (fermeture au clic extérieur)
const statusDropdownRef: Ref<HTMLElement | null> = ref(null);
const visibilityDropdownRef: Ref<HTMLElement | null> = ref(null);
const ownershipDropdownRef: Ref<HTMLElement | null> = ref(null);
const actionsDropdownRef: Ref<HTMLElement | null> = ref(null);

// Store d'authentification
const authStore = useAuthStore();
const collectionsStore = useCollectionsStore();
const { collections } = storeToRefs(collectionsStore);
const currentUserId: ComputedRef<string> = computed(() => authStore.user?.uuid || '');

// Options de tri avec types stricts
const sortOptions: ComputedRef<SortOption[]> = computed(() => [
  { value: 'date', label: 'Date' },
  { value: 'title', label: 'Nom' },
  { value: 'location', label: 'Lieu' },
  { value: 'material', label: 'Entité matérielle' },
  { value: 'schema_type', label: 'Type de schéma' },
]);

// Handlers avec types de retour explicites
const handleUpdateSortBy = (value: string): void => {
  sortBy.value = value as SortBy;
};

const handleUpdateSearchQuery = (value: string): void => {
  searchQuery.value = value;
};

const handleToggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const handleToggleView = (): void => {
  isCardView.value = !isCardView.value;
  localStorage.setItem('memosViewMode', isCardView.value ? 'card' : 'table');
};

const handleToggleMobileMenu = (): void => {
  showMobileMenu.value = !showMobileMenu.value;
};

const toggleActionsDropdown = (): void => {
  showActionsDropdown.value = !showActionsDropdown.value;
  showStatusDropdown.value = false;
  showVisibilityDropdown.value = false;
  showOwnershipDropdown.value = false;
};

// Handlers pour les dropdowns
const toggleStatusDropdown = (): void => {
  showStatusDropdown.value = !showStatusDropdown.value;
  showVisibilityDropdown.value = false;
  showOwnershipDropdown.value = false;
  showActionsDropdown.value = false;
};

const toggleVisibilityDropdown = (): void => {
  showVisibilityDropdown.value = !showVisibilityDropdown.value;
  showStatusDropdown.value = false;
  showOwnershipDropdown.value = false;
  showActionsDropdown.value = false;
};

const toggleOwnershipDropdown = (): void => {
  showOwnershipDropdown.value = !showOwnershipDropdown.value;
  showStatusDropdown.value = false;
  showVisibilityDropdown.value = false;
  showActionsDropdown.value = false;
};

// Handlers pour les filtres de statut avec logique "anti-vide"
const toggleDraftStatus = (): void => {
  // Si on décoche et qu'il ne reste que validé, on le fait
  // Si on décoche et que validé est déjà décoché, on re-coche validé
  if (showDrafts.value && !showValidated.value) {
    showValidated.value = true;
  }
  showDrafts.value = !showDrafts.value;
};

const toggleValidatedStatus = (): void => {
  // Si on décoche et qu'il ne reste que brouillon, on le fait
  // Si on décoche et que brouillon est déjà décoché, on re-coche brouillon
  if (showValidated.value && !showDrafts.value) {
    showDrafts.value = true;
  }
  showValidated.value = !showValidated.value;
};

const toggleModelType = (): void => {
  // Si on décoche et qu'il ne reste que fiches, on le fait
  // Si on décoche et que fiches est déjà décoché, on re-coche fiches
  if (showModels.value && !showSheets.value) {
    showSheets.value = true;
  }
  showModels.value = !showModels.value;
};

const toggleSheetType = (): void => {
  // Si on décoche et qu'il ne reste que modèles, on le fait
  // Si on décoche et que modèles est déjà décoché, on re-coche modèles
  if (showSheets.value && !showModels.value) {
    showModels.value = true;
  }
  showSheets.value = !showSheets.value;
};

// Handlers pour les filtres de visibilité et propriétaire
const setVisibilityFilter = (filter: VisibilityFilter): void => {
  visibilityFilter.value = filter;
  showVisibilityDropdown.value = false;
};

const setOwnershipFilter = (filter: OwnershipFilter): void => {
  ownershipFilter.value = filter;
  showOwnershipDropdown.value = false;
};

const handlePreviousPage = (): void => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleNextPage = (): void => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

/*
const handleExportData = (): void => {
  const timestamp: string = new Date().toISOString().replace(/[:.]/g, '-');
  const dataToExport: Memo[] = filteredMemos.value;
  const jsonString: string = JSON.stringify(dataToExport, null, 2);
  const blob: Blob = new Blob([jsonString], { type: 'application/json' });
  const url: string = URL.createObjectURL(blob);
  
  const a: HTMLAnchorElement = document.createElement('a');
  a.href = url;
  a.download = `memos-export-${timestamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  emit('export', dataToExport);
};
*/
const handleExportSelection = (): void => {
  const memosToExport = props.memos.filter(memo => selectedMemos.value.includes(memo.uuid));
  if (memosToExport.length === 0) {
    toast.info('Veuillez sélectionner des mémos à exporter.');
    return;
  }
  
  const timestamp: string = new Date().toISOString().replace(/[:.]/g, '-');
  const jsonString: string = JSON.stringify(memosToExport, null, 2);
  const blob: Blob = new Blob([jsonString], { type: 'application/json' });
  const url: string = URL.createObjectURL(blob);
  
  const a: HTMLAnchorElement = document.createElement('a');
  a.href = url;
  a.download = `memos-selection-export-${timestamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  toast.success(`${memosToExport.length} mémo(s) exporté(s).`);
  emit('export', memosToExport);
  showActionsDropdown.value = false;
};

// Handlers pour les événements de MemosCard
const handleView = (uuid: string, memoObject?: Memo): void => {
  // Si l'objet memo est fourni, on l'utilise, sinon on le trouve dans la liste
  const memo = memoObject || props.memos.find((m: Memo) => m.uuid === uuid);
  if (memo) {
    emit('view', memo);
  }
};

const handleEdit = (uuid: string, memoObject?: Memo): void => {
  // Si l'objet memo est fourni, on l'utilise, sinon on le trouve dans la liste
  const memo = memoObject || props.memos.find((m: Memo) => m.uuid === uuid);
  if (memo) {
    emit('edit', memo);
  }
};

const handleDelete = (uuid: string): void => {
  // Trouve le memo correspondant et l'émet
  const memo = props.memos.find((m: Memo) => m.uuid === uuid);
  if (memo) {
    emit('delete', memo);
  }
};

// Computed avec types stricts
const statusFilterLabel: ComputedRef<string> = computed(() => {
  const activeFilters: string[] = [];
  if (showDrafts.value) activeFilters.push('Brouillon');
  if (showValidated.value) activeFilters.push('Validé');
  if (showModels.value) activeFilters.push('Modèle');
  if (showSheets.value) activeFilters.push('Fiche');
  
  if (activeFilters.length === 0) return 'Aucun';
  if (activeFilters.length === 1) return activeFilters[0];
  if (activeFilters.length === 4) return 'Tous';
  return `${activeFilters.length} types`;
});

const statusFilterIcon: ComputedRef<typeof FileIcon> = computed(() => {
  const activeCount = [showDrafts.value, showValidated.value, showModels.value, showSheets.value].filter(Boolean).length;
  if (activeCount === 0) return FileIcon;
  if (activeCount === 1) {
    if (showDrafts.value) return FileEditIcon;
    if (showValidated.value) return FileCheckIcon;
    if (showModels.value) return BookTemplateIcon;
    if (showSheets.value) return FileIcon;
  }
  return FileIcon;
});

const statusFilterColor: ComputedRef<string> = computed(() => {
  const activeCount = [showDrafts.value, showValidated.value, showModels.value, showSheets.value].filter(Boolean).length;
  if (activeCount === 0) return 'text-gray-400';
  if (activeCount === 4) return 'text-blue-600';
  return 'text-gray-700';
});

const visibilityFilterLabel: ComputedRef<string> = computed(() => {
  switch (visibilityFilter.value) {
    case 'public':
      return 'Public';
    case 'private':
      return 'Privé';
    default:
      return 'Tous';
  }
});

const visibilityFilterIcon: ComputedRef<typeof EyeIcon> = computed(() => {
  switch (visibilityFilter.value) {
    case 'public':
      return GlobeIcon;
    case 'private':
      return ShieldIcon;
    default:
      return EyeIcon;
  }
});

const visibilityFilterColor: ComputedRef<string> = computed(() => {
  return visibilityFilter.value === 'all' ? 'text-gray-600' : 'text-blue-600';
});

const visibilityOptions = computed(() => [
  {
    value: 'all' as VisibilityFilter,
    label: 'Tous',
    icon: EyeIcon
  },
  {
    value: 'public' as VisibilityFilter,
    label: 'Public',
    icon: GlobeIcon
  },
  {
    value: 'private' as VisibilityFilter,
    label: 'Privé',
    icon: ShieldIcon
  }
]);

const ownershipFilterLabel: ComputedRef<string> = computed(() => {
  switch (ownershipFilter.value) {
    case 'my':
      return 'À moi';
    case 'public':
      return 'Publics';
    default:
      return 'Tous';
  }
});

const ownershipFilterIcon: ComputedRef<typeof FileIcon> = computed(() => {
  switch (ownershipFilter.value) {
    case 'my':
      return UserIcon;
    case 'public':
      return UsersIcon;
    default:
      return FileIcon;
  }
});

const ownershipFilterColor: ComputedRef<string> = computed(() => {
  return ownershipFilter.value === 'all' ? 'text-gray-600' : 'text-purple-600';
});

const ownershipOptions = computed(() => [
  {
    value: 'all' as OwnershipFilter,
    label: 'Tous',
    icon: FileIcon
  },
  {
    value: 'my' as OwnershipFilter,
    label: 'À moi',
    icon: UserIcon
  },
  {
    value: 'public' as OwnershipFilter,
    label: 'Publics',
    icon: UsersIcon
  }
]);

// Fonction de recherche typée correctement
const searchInObject = (obj: Record<string, unknown> | unknown, query: string): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  
  const objectToSearch = obj as Record<string, unknown>;
  
  for (const key in objectToSearch) {
    const value = objectToSearch[key];
    
    if (typeof value === 'string' && value.toLowerCase().includes(query)) {
      return true;
    }
    
    if (typeof value === 'object' && value !== null) {
      if (searchInObject(value as Record<string, unknown>, query)) {
        return true;
      }
    }
    
    if (Array.isArray(value)) {
      for (const item of value) {
        if (searchInObject(item as Record<string, unknown>, query)) {
          return true;
        }
      }
    }
  }
  
  return false;
};

// Filtrage et tri avec types stricts
const filteredMemos: ComputedRef<Memo[]> = computed(() => {
  let result: Memo[] = [...props.memos];

  // Filtrage par ownership (propriétaire)
  if (ownershipFilter.value !== 'all') {
    result = result.filter((memo: Memo): boolean => {
      if (ownershipFilter.value === 'my') {
        return memo.user_id === currentUserId.value;
      } else if (ownershipFilter.value === 'public') {
        return memo.is_public && memo.user_id !== currentUserId.value;
      }
      return true;
    });
  }

  // Filtrage par statut
  result = result.filter((memo: Memo): boolean => {
    // Filtrage par type (modèle/fiche)
    if (memo.is_model && !showModels.value) return false;
    if (!memo.is_model && !showSheets.value) return false;
    
    // Filtrage par état de validation (brouillon/validé)
    if (memo.is_draft && !showDrafts.value) return false;
    if (!memo.is_draft && !showValidated.value) return false;
    
    return true;
  });

  // Filtrage par visibilité
  if (visibilityFilter.value !== 'all') {
    result = result.filter((memo: Memo): boolean => 
      visibilityFilter.value === 'public' ? memo.is_public : !memo.is_public
    );
  }

  // Filtrage par recherche
  if (searchQuery.value) {
    const query: string = searchQuery.value.toLowerCase();
    result = result.filter((memo: Memo): boolean => searchInObject(memo, query));
  }

  // Tri
  result.sort((a: Memo, b: Memo): number => {
    let comparison: number = 0;
    switch (sortBy.value) {
      case 'date':
        comparison = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        break;
      case 'title':
        comparison = (a.title || '').localeCompare(b.title || '');
        break;
      case 'location':
        comparison = (a.location?.site || '').localeCompare(b.location?.site || '');
        break;
      case 'material':
        comparison = (a.material_entity?.appellation || '').localeCompare(b.material_entity?.appellation || '');
        break;
      case 'schema_type':
        comparison = (a.schema_model?.type || '').localeCompare(b.schema_model?.type || '');
        break;
    }
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return result;
});

// Pagination avec types stricts
const totalPages: ComputedRef<number> = computed(() => Math.ceil(filteredMemos.value.length / itemsPerPage.value));
const startIndex: ComputedRef<number> = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex: ComputedRef<number> = computed(() => startIndex.value + itemsPerPage.value);

const paginatedMemos: ComputedRef<Memo[]> = computed(() => {
  return filteredMemos.value.slice(startIndex.value, endIndex.value);
});

// Gestion de fermeture des dropdowns au clic extérieur
const handleClickOutside = (event: Event): void => {
  const target = event.target as HTMLElement;
  
  if (statusDropdownRef.value && !statusDropdownRef.value.contains(target)) {
    showStatusDropdown.value = false;
  }
  
  if (visibilityDropdownRef.value && !visibilityDropdownRef.value.contains(target)) {
    showVisibilityDropdown.value = false;
  }
  
  if (ownershipDropdownRef.value && !ownershipDropdownRef.value.contains(target)) {
    showOwnershipDropdown.value = false;
  }
  
  if (actionsDropdownRef.value && !actionsDropdownRef.value.contains(target)) {
    showActionsDropdown.value = false;
  }
};

// Lifecycle hooks
onMounted((): void => {
  document.addEventListener('click', handleClickOutside);
  collectionsStore.fetchCollections();
});

onUnmounted((): void => {
  document.removeEventListener('click', handleClickOutside);
});

// Watchers avec types stricts
watch([searchQuery, showDrafts, showValidated, showModels, showSheets, sortBy, visibilityFilter, ownershipFilter], (): void => {
  currentPage.value = 1;
});

const toast = useToast();

// Gestion de la sélection
const selectedMemos = ref<string[]>([]);

const isSelectionMode = computed(() => selectedMemos.value.length > 0);

const isMemoSelected = (uuid: string): boolean => {
  return selectedMemos.value.includes(uuid);
};

const toggleMemoSelection = (uuid: string): void => {
  const index = selectedMemos.value.indexOf(uuid);
  if (index === -1) {
    selectedMemos.value.push(uuid);
  } else {
    selectedMemos.value.splice(index, 1);
  }
};

const areAllVisibleMemosSelected = computed(() => {
  const visibleUuids = paginatedMemos.value.map(m => m.uuid);
  return visibleUuids.length > 0 && visibleUuids.every(uuid => selectedMemos.value.includes(uuid));
});

const isAnyMemoSelected = computed(() => {
    return selectedMemos.value.length > 0 && !areAllVisibleMemosSelected.value;
});

// Vérifier si on peut supprimer tous les mémos sélectionnés (seuls les mémos du user peuvent être supprimés)
const canDeleteSelectedMemos = computed(() => {
  if (selectedMemos.value.length === 0) return false;
  
  const selectedMemoObjects = props.memos.filter(memo => selectedMemos.value.includes(memo.uuid));
  return selectedMemoObjects.every(memo => memo.user_id === authStore.user?.uuid);
});

const toggleSelectAll = (): void => {
  if (areAllVisibleMemosSelected.value) {
    // Déselectionner tout ce qui est visible
    const visibleUuids = paginatedMemos.value.map(m => m.uuid);
    selectedMemos.value = selectedMemos.value.filter(uuid => !visibleUuids.includes(uuid));
  } else {
    // Sélectionner tout ce qui est visible et pas déjà sélectionné
    const visibleUuids = paginatedMemos.value.map(m => m.uuid);
    visibleUuids.forEach(uuid => {
      if (!selectedMemos.value.includes(uuid)) {
        selectedMemos.value.push(uuid);
      }
    });
  }
};

const handleAddMemosToCollection = async (collectionUuid: string): Promise<void> => {
  showActionsDropdown.value = false;
  const memosToAdd = [...selectedMemos.value];
  if (memosToAdd.length === 0) return;

  const collection = collections.value.find(c => c.uuid === collectionUuid);
  const collectionName = collection?.name || 'Collection inconnue';
  
  let successCount = 0;
  let alreadyInCollectionCount = 0;
  let errorCount = 0;

  try {
    // Traiter chaque mémo individuellement pour gérer les erreurs 409
    const results = await Promise.allSettled(
      memosToAdd.map(memoUuid => 
        collectionsStore.addMemoToCollection(collectionUuid, memoUuid)
      )
    );

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value !== null) {
        successCount++;
      } else if (result.status === 'rejected') {
        const error = result.reason;
        // Vérifier si c'est une erreur 409 (déjà dans la collection)
        if (error?.message?.includes('already in this collection') || 
            error?.response?.status === 409) {
          alreadyInCollectionCount++;
        } else {
          errorCount++;
          console.error(`Erreur lors de l'ajout du mémo ${memosToAdd[index]}:`, error);
        }
      }
    });

    // Construire le message de notification
    if (successCount > 0) {
      emit('collection-memo-added', { 
        memoCount: successCount, 
        collectionName,
        alreadyInCollection: alreadyInCollectionCount,
        errors: errorCount
      });
    } else if (alreadyInCollectionCount > 0 && errorCount === 0) {
      // Tous les mémos sont déjà dans la collection
      emit('collection-memo-added', { 
        memoCount: 0, 
        collectionName,
        alreadyInCollection: alreadyInCollectionCount,
        errors: 0
      });
    }

    selectedMemos.value = []; // Clear selection
    
  } catch (error) {
    console.error("Erreur lors de l'ajout des mémos:", error);
    // En cas d'erreur globale, on affiche quand même une notification
    emit('collection-memo-added', { 
      memoCount: 0, 
      collectionName,
      alreadyInCollection: 0,
      errors: memosToAdd.length
    });
  }
};

const handleOpenCollectionCreator = (): void => {
  showActionsDropdown.value = false;
  isCollectionEditorOpen.value = true;
};

const handleCollectionSaved = async (newCollection: MemoCollection): Promise<void> => {
  isCollectionEditorOpen.value = false;
  await collectionsStore.fetchCollections();
  if (selectedMemos.value.length > 0) {
    await handleAddMemosToCollection(newCollection.uuid);
  }
};

const handleRemoveFromCollection = async (): Promise<void> => {
  if (!props.collection) return;
  
  const collectionUuid = props.collection.uuid;
  const memosToRemove = [...selectedMemos.value];

  try {
    await Promise.all(
      memosToRemove.map(memoUuid => 
        collectionsStore.removeMemoFromCollection(collectionUuid, memoUuid)
      )
    );
    
    // Émettre l'événement au lieu d'utiliser toast
    emit('collection-memo-removed', { 
      memoCount: memosToRemove.length, 
      collectionName: props.collection.name 
    });
    
    // Vider la sélection et rafraîchir la liste des mémos
    selectedMemos.value = [];
    emit('refresh-data');

  } catch (error) {
    console.error("Erreur lors du retrait des mémos:", error);
  }
};

const handleCreateMemo = (): void => {
  if (props.collection) {
    // Vérifier les permissions avant d'ouvrir le modal de sélection
    if (canEditCollection.value) {
      isMemoSelectionModalOpen.value = true;
    }
  } else {
    // Sinon, créer un nouveau mémo
    emit('create-memo');
  }
};

const handleDeleteSelection = (): void => {
  showActionsDropdown.value = false;
  if (!canDeleteSelectedMemos.value) return;
  
  emit('delete-memos', selectedMemos.value);
};

// Computed properties pour le modal
const allMemos = computed(() => {
  // Utiliser les mémos accessibles passés en props, sinon fallback sur les mémos de la collection
  return props.allAccessibleMemos.length > 0 ? props.allAccessibleMemos : props.memos;
});

// Vérification des permissions pour éditer la collection
const canEditCollection = computed(() => {
  if (!props.collection) return false;
  
  // Les admins peuvent toujours éditer
  if (authStore.isAdmin) return true;
  
  // Les utilisateurs normaux ne peuvent éditer que leurs propres collections
  return props.collection.user_id === currentUserId.value;
});

const currentUserMemos = computed(() => {
  return allMemos.value.filter(memo => memo.user_id === currentUserId.value);
});

const existingMemosInCollection = computed(() => {
  return props.memos.map(memo => memo.uuid);
});

// Méthodes pour le modal
const closeMemoSelectionModal = (): void => {
  isMemoSelectionModalOpen.value = false;
};

const handleAddMemosFromModal = async (memoUuids: string[]): Promise<void> => {
  if (!props.collection) return;
  
  const collectionUuid = props.collection.uuid;
  const collectionName = props.collection.name;
  
  let successCount = 0;
  let alreadyInCollectionCount = 0;
  let errorCount = 0;

  try {
    // Traiter chaque mémo individuellement pour gérer les erreurs 409
    const results = await Promise.allSettled(
      memoUuids.map(memoUuid => 
        collectionsStore.addMemoToCollection(collectionUuid, memoUuid)
      )
    );

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value !== null) {
        successCount++;
      } else if (result.status === 'rejected') {
        const error = result.reason;
        // Vérifier si c'est une erreur 409 (déjà dans la collection)
        if (error?.message?.includes('already in this collection') || 
            error?.response?.status === 409) {
          alreadyInCollectionCount++;
        } else {
          errorCount++;
          console.error(`Erreur lors de l'ajout du mémo ${memoUuids[index]}:`, error);
        }
      }
    });

    // Émettre l'événement
    emit('collection-memo-added', { 
      memoCount: successCount, 
      collectionName,
      alreadyInCollection: alreadyInCollectionCount,
      errors: errorCount
    });
    
  } catch (error) {
    console.error("Erreur lors de l'ajout des mémos:", error);
    emit('collection-memo-added', { 
      memoCount: 0, 
      collectionName,
      alreadyInCollection: 0,
      errors: memoUuids.length
    });
  }
};

const handleUpdateCollectionFromModal = async (data: { 
  added: string[]; 
  removed: string[]; 
  collectionName: string;
}): Promise<void> => {
  if (!props.collection) return;
  
  const collectionUuid = props.collection.uuid;
  const collectionName = props.collection.name;
  
  let addedSuccessCount = 0;
  let removedSuccessCount = 0;
  let addedErrorCount = 0;
  let removedErrorCount = 0;
  let alreadyInCollectionCount = 0;

  try {
    // Ajouter les nouveaux mémos
    if (data.added.length > 0) {
      const addResults = await Promise.allSettled(
        data.added.map(memoUuid => 
          collectionsStore.addMemoToCollection(collectionUuid, memoUuid)
        )
      );

      addResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value !== null) {
          addedSuccessCount++;
        } else if (result.status === 'rejected') {
          const error = result.reason;
          if (error?.message?.includes('already in this collection') || 
              error?.response?.status === 409) {
            alreadyInCollectionCount++;
          } else {
            addedErrorCount++;
            console.error(`Erreur lors de l'ajout du mémo ${data.added[index]}:`, error);
          }
        }
      });
    }

    // Retirer les mémos
    if (data.removed.length > 0) {
      const removeResults = await Promise.allSettled(
        data.removed.map(memoUuid => 
          collectionsStore.removeMemoFromCollection(collectionUuid, memoUuid)
        )
      );

      removeResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          removedSuccessCount++;
        } else {
          removedErrorCount++;
          console.error(`Erreur lors du retrait du mémo ${data.removed[index]}:`, result.reason);
        }
      });
    }

    // Émettre les événements appropriés
    if (addedSuccessCount > 0) {
      emit('collection-memo-added', { 
        memoCount: addedSuccessCount, 
        collectionName,
        alreadyInCollection: alreadyInCollectionCount,
        errors: addedErrorCount
      });
    }

    if (removedSuccessCount > 0) {
      emit('collection-memo-removed', { 
        memoCount: removedSuccessCount, 
        collectionName
      });
    }

    // Si il y a des erreurs, les afficher
    if (addedErrorCount > 0 || removedErrorCount > 0) {
      console.error(`Erreurs lors de la mise à jour: ${addedErrorCount} ajouts, ${removedErrorCount} suppressions`);
    }
    
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la collection:", error);
  }
};
</script>

<style scoped>
.anamnesis-tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 9999;
  top: -3rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.25rem 0.75rem;
  pointer-events: none;
}

.anamnesis-tooltip-content {
  background-color: rgb(17, 24, 39);
  color: white;
  font-size: 0.75rem;
  border-radius: 0.375rem;
  padding: 0.5rem;
  white-space: nowrap;
}

.anamnesis-tooltip-arrow {
  position: absolute;
  left: 50%;
  bottom: -0.25rem;
  transform: translateX(-50%) rotate(45deg);
  width: 1rem;
  height: 1rem;
  background-color: rgb(17, 24, 39);
}

.group:hover .anamnesis-tooltip {
  visibility: visible;
}

.anamnesis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

.collection-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}
</style>
